import type {
    BindExistingIgAutomationRequest,
    ConnectedPlatformRow,
    CreateSocialPostRequest,
    InstagramAutomation,
    InstagramAutomationInput,
    InstagramAutomationMediaItem,
    InstagramAutomationStatusPayload,
    SocialPlatform,
    SocialPostWithAutomation,
} from '../../types/instagramAutomation.types';
import { IErrorType } from '../../constants/types';

export const types = {
    GET_CONNECTED_PLATFORMS: 'GET_CONNECTED_PLATFORMS',
    GET_CONNECTED_PLATFORMS_SUCCESS: 'GET_CONNECTED_PLATFORMS_SUCCESS',
    GET_CONNECTED_PLATFORMS_FAIL: 'GET_CONNECTED_PLATFORMS_FAIL',

    GET_INSTAGRAM_AUTOMATION_STATUS: 'GET_INSTAGRAM_AUTOMATION_STATUS',
    GET_INSTAGRAM_AUTOMATION_STATUS_SUCCESS: 'GET_INSTAGRAM_AUTOMATION_STATUS_SUCCESS',
    GET_INSTAGRAM_AUTOMATION_STATUS_FAIL: 'GET_INSTAGRAM_AUTOMATION_STATUS_FAIL',

    LOAD_POST_AUTOMATION_CONTEXT: 'LOAD_POST_AUTOMATION_CONTEXT',
    LOAD_POST_AUTOMATION_CONTEXT_SUCCESS: 'LOAD_POST_AUTOMATION_CONTEXT_SUCCESS',
    LOAD_POST_AUTOMATION_CONTEXT_FAIL: 'LOAD_POST_AUTOMATION_CONTEXT_FAIL',

    GET_INSTAGRAM_AUTOMATION_OAUTH_URL: 'GET_INSTAGRAM_AUTOMATION_OAUTH_URL',
    GET_INSTAGRAM_AUTOMATION_OAUTH_URL_SUCCESS: 'GET_INSTAGRAM_AUTOMATION_OAUTH_URL_SUCCESS',
    GET_INSTAGRAM_AUTOMATION_OAUTH_URL_FAIL: 'GET_INSTAGRAM_AUTOMATION_OAUTH_URL_FAIL',

    EXCHANGE_META_OAUTH: 'EXCHANGE_META_OAUTH',
    EXCHANGE_META_OAUTH_SUCCESS: 'EXCHANGE_META_OAUTH_SUCCESS',
    EXCHANGE_META_OAUTH_FAIL: 'EXCHANGE_META_OAUTH_FAIL',

    CREATE_SOCIAL_POST: 'CREATE_SOCIAL_POST',
    CREATE_SOCIAL_POST_SUCCESS: 'CREATE_SOCIAL_POST_SUCCESS',
    CREATE_SOCIAL_POST_FAIL: 'CREATE_SOCIAL_POST_FAIL',

    PUBLISH_SOCIAL_POST: 'PUBLISH_SOCIAL_POST',
    PUBLISH_SOCIAL_POST_SUCCESS: 'PUBLISH_SOCIAL_POST_SUCCESS',
    PUBLISH_SOCIAL_POST_FAIL: 'PUBLISH_SOCIAL_POST_FAIL',

    GET_SOCIAL_POST: 'GET_SOCIAL_POST',
    GET_SOCIAL_POST_SUCCESS: 'GET_SOCIAL_POST_SUCCESS',
    GET_SOCIAL_POST_FAIL: 'GET_SOCIAL_POST_FAIL',

    CONFIGURE_INSTAGRAM_AUTOMATION: 'CONFIGURE_INSTAGRAM_AUTOMATION',
    CONFIGURE_INSTAGRAM_AUTOMATION_SUCCESS: 'CONFIGURE_INSTAGRAM_AUTOMATION_SUCCESS',
    CONFIGURE_INSTAGRAM_AUTOMATION_FAIL: 'CONFIGURE_INSTAGRAM_AUTOMATION_FAIL',

    PUBLISH_SOCIAL_POST_FLOW: 'PUBLISH_SOCIAL_POST_FLOW',
    PUBLISH_SOCIAL_POST_FLOW_SUCCESS: 'PUBLISH_SOCIAL_POST_FLOW_SUCCESS',
    PUBLISH_SOCIAL_POST_FLOW_FAIL: 'PUBLISH_SOCIAL_POST_FLOW_FAIL',

    POLL_SOCIAL_POST_UNTIL_SETTLED: 'POLL_SOCIAL_POST_UNTIL_SETTLED',
    POLL_SOCIAL_POST_UNTIL_SETTLED_SUCCESS: 'POLL_SOCIAL_POST_UNTIL_SETTLED_SUCCESS',
    POLL_SOCIAL_POST_UNTIL_SETTLED_FAIL: 'POLL_SOCIAL_POST_UNTIL_SETTLED_FAIL',

    RESET_SOCIAL_POSTS_ERROR: 'RESET_SOCIAL_POSTS_ERROR',

    LIST_INSTAGRAM_AUTOMATION_POSTS: 'LIST_INSTAGRAM_AUTOMATION_POSTS',
    LIST_INSTAGRAM_AUTOMATION_POSTS_SUCCESS: 'LIST_INSTAGRAM_AUTOMATION_POSTS_SUCCESS',
    LIST_INSTAGRAM_AUTOMATION_POSTS_FAIL: 'LIST_INSTAGRAM_AUTOMATION_POSTS_FAIL',

    PATCH_SOCIAL_POST: 'PATCH_SOCIAL_POST',
    PATCH_SOCIAL_POST_SUCCESS: 'PATCH_SOCIAL_POST_SUCCESS',
    PATCH_SOCIAL_POST_FAIL: 'PATCH_SOCIAL_POST_FAIL',

    LIST_INSTAGRAM_AUTOMATION_MEDIA: 'LIST_INSTAGRAM_AUTOMATION_MEDIA',
    LIST_INSTAGRAM_AUTOMATION_MEDIA_SUCCESS: 'LIST_INSTAGRAM_AUTOMATION_MEDIA_SUCCESS',
    LIST_INSTAGRAM_AUTOMATION_MEDIA_FAIL: 'LIST_INSTAGRAM_AUTOMATION_MEDIA_FAIL',

    BIND_EXISTING_IG_AUTOMATION: 'BIND_EXISTING_IG_AUTOMATION',
    BIND_EXISTING_IG_AUTOMATION_SUCCESS: 'BIND_EXISTING_IG_AUTOMATION_SUCCESS',
    BIND_EXISTING_IG_AUTOMATION_FAIL: 'BIND_EXISTING_IG_AUTOMATION_FAIL',
};

export interface ICacheableFetch {
    skipRefresh?: boolean;
}

export interface IGetConnectedPlatforms extends ICacheableFetch {}

export interface IGetInstagramAutomationStatus extends ICacheableFetch {}

export interface ILoadPostAutomationContext extends ICacheableFetch {
    /** Refresh in background without blanking the modal */
    silent?: boolean;
}

export interface ILoadPostAutomationContextSuccess {
    automationStatus: InstagramAutomationStatusPayload;
    connectedPlatforms: ConnectedPlatformRow[];
}

export interface IExchangeMetaOAuth {
    code: string;
}

export interface ICreateSocialPost {
    body: CreateSocialPostRequest;
}

export interface IPublishSocialPost {
    postId: string;
}

export interface IGetSocialPost {
    postId: string;
}

export interface IConfigureInstagramAutomation {
    socialPostId: string;
    config: InstagramAutomationInput;
}

export interface IPublishSocialPostFlow {
    body: CreateSocialPostRequest;
    configureAutomation?: InstagramAutomationInput;
}

export interface IListInstagramAutomationPosts extends ICacheableFetch {
    page?: number;
    limit?: number;
}

export interface IListInstagramAutomationPostsSuccess {
    posts: SocialPostWithAutomation[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
}

export interface IPatchSocialPost {
    postId: string;
    patch: {
        instagram_automation?: InstagramAutomationInput;
        caption?: string;
        scheduled_at?: string;
    };
}

export interface IPatchSocialPostFail {
    postId: string;
    error: IErrorType | string;
}

export interface IListInstagramAutomationMedia {
    limit?: number;
    after?: string;
}

export interface IBindExistingIgAutomation {
    body: BindExistingIgAutomationRequest;
}

export interface ISocialPostsInitialState {
    connectedPlatforms: ConnectedPlatformRow[];
    automationStatus: InstagramAutomationStatusPayload | null;
    oauthUrl: string | null;
    postsById: Record<string, SocialPostWithAutomation>;
    instagramPostsList: SocialPostWithAutomation[];
    instagramPostsListTotal: number;
    instagramPostsListPage: number;
    instagramPostsListTotalPages: number;
    loadingConnectedPlatforms: boolean;
    loadingAutomationStatus: boolean;
    loadingContext: boolean;
    loadingOAuthUrl: boolean;
    exchangingOAuth: boolean;
    publishing: boolean;
    publishingPlatform: SocialPlatform | null;
    loadingInstagramPostsList: boolean;
    togglingAutomationPostIds: Record<string, boolean>;
    bindingExistingAutomation: boolean;
    error: IErrorType | string | null;
}

export type { InstagramAutomation, InstagramAutomationMediaItem, SocialPostWithAutomation };
